/**
 * Sumber log sebuah plot: kurva apa, dari sumur mana, pada kedalaman berapa.
 *
 * Crossplot dan Pickett masing-masing punya satu, dengan lingkupnya sendiri.
 * Dulu Pickett "mengikuti lingkup" crossplot lalu menimpanya dengan sumur dan
 * selangnya sendiri, dan keduanya berbeda bentuk — orang yang memilih sumur di
 * satu tempat tidak bisa menebak apakah yang lain ikut. Sekarang keduanya
 * bertanya dengan kata-kata yang sama dan menyimpan jawabannya terpisah.
 *
 * Tiga pertanyaan, berurutan seperti orang menanyakannya:
 *
 * - **Kurva** — diisi pemakainya: X, Y, dan skala warna untuk crossplot;
 *   resistivitas dan porositas untuk Pickett.
 * - **Sumur** — satu sumur, beberapa sumur yang dicentang, atau sumur di
 *   sekitar sumur acuan dalam jari-jari tertentu.
 * - **Kedalaman** — seluruhnya, MD dari–sampai, antara dua marker, atau zona.
 */

import { useMemo, useState, type ReactNode } from "react";

import type { Screening, Well } from "../types";
import {
  StackedDropdown,
  boundText,
  depthModeOf,
  loggedWells,
  markerRows,
  wellModeOf,
  withChosen,
  zoneNames,
  type DepthMode,
  type Scope,
  type WellMode,
} from "./DataScope";
import {
  Checkbox,
  Chip,
  Dropdown,
  Empty,
  Notice,
  NumberField,
  OptionalNumberField,
  Panel,
  Segmented,
  Slider,
} from "./ui";

/** Pilihan "semua field" pada penyaring; bukan nama lapangan mana pun. */
const ALL_FIELDS = "__semua_field__";

const NOTE = { font: "400 9.5px var(--font-sans)", color: "var(--ink-6)", lineHeight: 1.6 } as const;
const HEAD = { font: "600 9.5px var(--font-mono)", color: "var(--ink-5)", letterSpacing: "0.06em" } as const;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7, paddingTop: 4 }}>
      <span style={HEAD}>{title}</span>
      {children}
    </div>
  );
}

/** Pemilih sumur berlabel di atasnya — label di samping mendorongnya keluar dari rel. */
function WellPicker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: number;
  options: { value: number; label: string }[];
  onChange: (id: number) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      <Dropdown value={value} options={options} onChange={onChange} width="100%" />
    </div>
  );
}

/**
 * Lingkup sesudah berpindah mode sumur. Isian mode yang ditinggalkan tetap
 * tersimpan; `effectiveScope` yang memastikan hanya milik mode terpilih yang
 * dikirim.
 */
export function withWellMode(scope: Scope, mode: WellMode, wells: Well[], activeWellId: number | null): Scope {
  const fallback = activeWellId ?? loggedWells(wells)[0]?.id ?? wells[0]?.id ?? null;
  if (mode === "single") {
    const id = scope.wells[0] ?? scope.centerWell ?? fallback;
    return { ...scope, wellMode: mode, wells: id === null ? [] : [id] };
  }
  if (mode === "radius") {
    return { ...scope, wellMode: mode, centerWell: scope.centerWell ?? scope.wells[0] ?? fallback };
  }
  return { ...scope, wellMode: mode };
}

/** Lingkup sesudah berpindah mode kedalaman; selang marker langsung terisi puncaknya. */
export function withDepthMode(scope: Scope, mode: DepthMode, firstMarker: string | undefined): Scope {
  const next: Scope = { ...scope, depthMode: mode };
  if (mode === "markers" && !scope.topRef.startsWith("marker:") && firstMarker) {
    next.topRef = `marker:${firstMarker}`;
    if (!scope.baseRef.startsWith("marker:")) next.baseRef = "";
  }
  return next;
}

export function LogSource({
  title,
  curves,
  wells,
  scope,
  onChange,
  activeWellId = null,
  screening,
  rockFilter = false,
}: {
  title: string;
  /** Pemilih kurva, disusun pemakainya; berdiri paling atas di panel ini. */
  curves: ReactNode;
  wells: Well[];
  scope: Scope;
  onChange: (next: Scope) => void;
  /** Sumur aktif di cangkang, bawaan saat berpindah ke Tunggal atau Radius. */
  activeWellId?: number | null;
  /** Hasil penjaringan radius dari jawaban plot, untuk menyebut cacahnya. */
  screening?: Screening | null;
  /** Penyaring Vsh sendiri, bagi plot yang tidak memakai panel Rock filter layar. */
  rockFilter?: boolean;
}) {
  const [query, setQuery] = useState("");
  const wellMode = wellModeOf(scope);
  const depthMode = depthModeOf(scope);
  const set = (patch: Partial<Scope>) => onChange({ ...scope, ...patch });

  // Sumur yang ikut menentukan marker mana yang bisa menjadi batas: yang
  // dipilih, atau seluruh lapangan untuk multi-tanpa-centang dan radius.
  const scoped = useMemo(() => {
    if (wellMode === "single") return wells.filter((w) => w.id === scope.wells[0]);
    if (wellMode === "multi" && scope.wells.length) return wells.filter((w) => scope.wells.includes(w.id));
    return wells;
  }, [wells, wellMode, scope.wells]);
  // Hanya marker sumur berkurva yang bisa menjadi batas: sumur tanpa kurva
  // sudah gugur sebelum selangnya sempat dipotong.
  const logged = useMemo(() => loggedWells(scoped), [scoped]);
  const markers = useMemo(() => markerRows(logged), [logged]);
  const hiddenMarkers = useMemo(() => markerRows(scoped).length - markers.length, [scoped, markers]);
  const carrying = (ref: string) =>
    markers.find((row) => `marker:${row.name}` === ref)?.wells ?? 0;
  const zones = useMemo(() => zoneNames(scoped), [scoped]);

  // Lapangan di dalam proyek. Prabumulih memuat dua puluh dua; proyek satu
  // lapangan tidak perlu pemilihnya, jadi ia hanya muncul bila ada lebih dari satu.
  const fields = useMemo(() => {
    const byField = new Map<string, number[]>();
    for (const w of wells) {
      const name = w.field?.trim() || "";
      byField.set(name, [...(byField.get(name) ?? []), w.id]);
    }
    return [...byField.entries()]
      .map(([name, ids]) => ({ name, ids }))
      .sort((a, b) => (a.name === "") !== (b.name === "") ? (a.name === "" ? 1 : -1) : a.name.localeCompare(b.name));
  }, [wells]);
  const manyFields = fields.filter((f) => f.name).length > 1;
  const [fieldFilter, setFieldFilter] = useState<string | null>(null);
  const inField = (w: Well) => fieldFilter === null || (w.field?.trim() || "") === fieldFilter;

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return wells.filter(
      (w) => inField(w) && (!needle || w.name.toLowerCase().includes(needle)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inField bergantung pada fieldFilter
  }, [wells, query, fieldFilter]);
  const chosen = new Set(scope.wells);

  const labelOf = (w: Well) =>
    `${w.name}${w.curve_sets.length ? "" : " · no curve"}${manyFields && w.field ? ` · ${w.field}` : ""}`;
  const wellOptions = wells
    .filter((w) => inField(w) || scope.wells.includes(w.id) || w.id === scope.centerWell)
    .map((w) => ({ value: w.id, label: labelOf(w) }));

  // Satu lapangan utuh dicentang atau dilepas sekaligus. "Semua" (daftar kosong)
  // lebih dulu dituliskan seluruhnya, seperti pada centang satu sumur.
  const fieldChosen = (ids: number[]) =>
    scope.wells.length === 0 || ids.every((id) => chosen.has(id));
  const toggleField = (ids: number[]) => {
    const base = scope.wells.length ? scope.wells : [];
    const next = fieldChosen(ids) && scope.wells.length
      ? base.filter((id) => !ids.includes(id))
      : [...new Set([...base, ...ids])];
    set({ wells: next.length === wells.length ? [] : next });
  };
  const markerOptions = markers.map((row) => ({ value: `marker:${row.name}`, label: row.name }));

  const toggleWell = (id: number) => {
    // Kosong berarti seluruh lapangan, jadi melepas satu sumur dari keadaan
    // itu lebih dulu menuliskan seluruhnya.
    const base = scope.wells.length ? scope.wells : wells.map((w) => w.id);
    const next = base.includes(id) ? base.filter((v) => v !== id) : [...base, id];
    set({ wells: next.length === wells.length ? [] : next });
  };

  return (
    <Panel title={title}>
      <Section title="KURVA">{curves}</Section>

      <Section title="SUMUR">
        <Segmented
          value={wellMode}
          size="sm"
          options={[
            { value: "single", label: "Tunggal" },
            { value: "multi", label: "Multi" },
            { value: "radius", label: "Radius" },
          ]}
          onChange={(mode) => onChange(withWellMode(scope, mode as WellMode, wells, activeWellId))}
        />

        {manyFields && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
            <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>FIELD</span>
            <Dropdown
              value={fieldFilter ?? ALL_FIELDS}
              options={[
                { value: ALL_FIELDS, label: `Semua field · ${wells.length} sumur` },
                ...fields.map((f) => ({
                  value: f.name,
                  label: `${f.name || "(tanpa field)"} · ${f.ids.length} sumur`,
                })),
              ]}
              onChange={(value) => setFieldFilter(value === ALL_FIELDS ? null : value)}
              width="100%"
            />
          </div>
        )}

        {wellMode === "single" && (
          <WellPicker
            label="SUMUR"
            value={scope.wells[0] ?? 0}
            options={scope.wells.length ? wellOptions : [{ value: 0, label: "—" }, ...wellOptions]}
            onChange={(id) => set({ wells: id ? [id] : [] })}
          />
        )}

        {wellMode === "multi" && (
          <>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cari sumur…"
              aria-label="Cari sumur di daftar ini"
              style={{
                height: 26,
                padding: "0 8px",
                background: "var(--panel-inset)",
                border: "1px solid var(--line-strong)",
                borderRadius: "var(--radius)",
                font: "400 11px var(--font-sans)",
                color: "var(--ink)",
                outline: "none",
              }}
            />
            {manyFields && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {fields.filter((f) => f.name).map((f) => (
                  <Chip key={f.name} active={scope.wells.length > 0 && fieldChosen(f.ids)}
                        onClick={() => toggleField(f.ids)}>
                    {f.name} {f.ids.length}
                  </Chip>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <Chip active={scope.wells.length === 0} onClick={() => set({ wells: [] })}>
                Semua
              </Chip>
              <Chip active={false} onClick={() => set({ wells: rows.map((w) => w.id) })}>
                Yang tersaring
              </Chip>
              <span style={{ ...NOTE, marginLeft: "auto" }}>
                {scope.wells.length || wells.length} dari {wells.length}
              </span>
            </div>
            <div className="scroll-y" style={{ maxHeight: 190, display: "flex", flexDirection: "column" }}>
              {rows.map((w) => (
                <Checkbox
                  key={w.id}
                  checked={scope.wells.length === 0 || chosen.has(w.id)}
                  onChange={() => toggleWell(w.id)}
                  label={w.name}
                  dot={w.panel_color || w.symbol_color}
                  meta={w.curve_sets.length ? undefined : "no curve"}
                />
              ))}
              {!rows.length && <Empty>Tidak ada sumur yang cocok.</Empty>}
            </div>
          </>
        )}

        {wellMode === "radius" && (
          <>
            <WellPicker
              label="SUMUR ACUAN"
              value={scope.centerWell ?? 0}
              options={scope.centerWell === null ? [{ value: 0, label: "—" }, ...wellOptions] : wellOptions}
              onChange={(id) => set({ centerWell: id || null })}
            />
            <NumberField
              label="Radius"
              unit="km"
              value={scope.radiusKm}
              min={0.01}
              step={0.5}
              onChange={(radiusKm) => set({ radiusKm })}
            />
            <Segmented
              value={scope.radiusMode}
              size="sm"
              options={[
                { value: "surface", label: "Kepala sumur" },
                { value: "trajectory", label: "Sepanjang lintasan" },
              ]}
              onChange={(radiusMode) => set({ radiusMode: radiusMode as Scope["radiusMode"] })}
            />
            <span style={NOTE}>
              {screening
                ? `${screening.kept} dari ${screening.asked} sumur terjaring dalam ${scope.radiusKm} km dari ${screening.center_name}, termasuk sumur acuannya.`
                : scope.radiusMode === "trajectory"
                  ? "Pusat lingkaran menuruni lintasan sumur acuan, jadi tetangga bisa terjaring pada kedalaman tertentu saja."
                  : "Satu lingkaran di kepala sumur acuan; sumur yang lolos ikut seluruh kedalamannya."}
            </span>
            {screening?.note && <Notice tone="warn">{screening.note}</Notice>}
          </>
        )}
      </Section>

      <Section title="KEDALAMAN">
        <Segmented
          value={depthMode}
          size="sm"
          options={[
            { value: "all", label: "Semua" },
            { value: "md", label: "MD" },
            { value: "markers", label: "Marker" },
            { value: "zones", label: "Zona" },
          ]}
          onChange={(mode) => onChange(withDepthMode(scope, mode as DepthMode, markers[0]?.name))}
        />

        {depthMode === "all" && <span style={NOTE}>Seluruh kedalaman tiap sumur yang berkurva.</span>}

        {depthMode === "md" && (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <OptionalNumberField label="MD DARI" unit="m" value={scope.mdMin} step={1}
                                     onChange={(mdMin) => set({ mdMin })} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <OptionalNumberField label="MD SAMPAI" unit="m" value={scope.mdMax} step={1}
                                     onChange={(mdMax) => set({ mdMax })} />
              </div>
            </div>
            {scope.mdMin !== null && scope.mdMax !== null && scope.mdMax <= scope.mdMin ? (
              <Notice tone="warn">MD sampai harus lebih dalam daripada MD dari.</Notice>
            ) : (
              <span style={NOTE}>
                Angka yang sama di setiap sumur. Yang dikosongkan berarti terbuka ke ujung data.
              </span>
            )}
          </>
        )}

        {depthMode === "markers" &&
          (markers.length ? (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <StackedDropdown
                  label="MARKER ATAS"
                  value={scope.topRef}
                  options={withChosen(markerOptions, scope.topRef, `${boundText(scope.topRef)} — no curve`)}
                  onChange={(topRef) => set({ topRef })}
                />
                <StackedDropdown
                  label="MARKER BAWAH"
                  value={scope.baseRef}
                  options={[
                    { value: "", label: "Marker berikutnya" },
                    ...withChosen(markerOptions, scope.baseRef, `${boundText(scope.baseRef)} — no curve`),
                  ]}
                  onChange={(baseRef) => set({ baseRef })}
                />
              </div>
              {/* Penyebutnya sumur berkurva, sebab pembilangnya pun dihitung dari sana. */}
              <span style={{ font: "400 9.5px var(--font-mono)", color: "var(--ink-6)" }}>
                {[scope.topRef, scope.baseRef]
                  .filter(Boolean)
                  .map((ref) => `${boundText(ref)} di ${carrying(ref)}`)
                  .join("; ")}{" "}
                dari {logged.length} sumur berkurva
              </span>
              <span style={NOTE}>
                Tiap sumur memakai kedalaman markernya sendiri; sumur yang tidak punya salah satu
                markernya dilewati dan disebut namanya.
              </span>
            </>
          ) : (
            <Notice tone="warn">
              {hiddenMarkers > 0
                ? `Markernya ada — ${hiddenMarkers} nama — tetapi seluruhnya di sumur yang belum berkurva, jadi tidak satu pun bisa membatasi selang. Pakai batas MD, atau muat log sumurnya lebih dulu di menu Data.`
                : "Sumur terpilih belum punya marker. Pakai batas MD, atau pasang markernya lebih dulu di menu Data."}
            </Notice>
          ))}

        {depthMode === "zones" &&
          (zones.length ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {zones.map((zone) => (
                <Chip
                  key={zone.name}
                  active={scope.zones.includes(zone.name)}
                  color={zone.color}
                  onClick={() =>
                    set({
                      zones: scope.zones.includes(zone.name)
                        ? scope.zones.filter((v) => v !== zone.name)
                        : [...scope.zones, zone.name],
                    })
                  }
                >
                  {zone.name}
                </Chip>
              ))}
            </div>
          ) : (
            <Empty>Sumur terpilih belum punya zona.</Empty>
          ))}
      </Section>

      {rockFilter && (
        <Section title="BATUAN">
          <Slider
            label="Vsh max"
            value={scope.vshMax}
            min={0}
            max={1}
            step={0.01}
            onChange={(vshMax) => set({ vshMax })}
            format={(v) => (v >= 1 ? "all" : v.toFixed(2))}
          />
        </Section>
      )}
    </Panel>
  );
}
